import React, { useCallback, useEffect, useState } from "react";
import useConfig from "../../demos/hooks/useConfig";
import {
  resolveDistanceApiBase,
  fetchDistanceBays,
  queryDistance,
  DistanceResult,
} from "./api/distanceClient";
import { DistanceHistoryEntry } from "./stores/distanceQueryStore";

const SwapIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m17 3 4 4-4 4" />
    <path d="M21 7H7" />
    <path d="m7 21-4-4 4-4" />
    <path d="M3 17h14" />
  </svg>
);

export const METRIC_LABELS: Record<string, string> = {
  walking: "Walking",
  direct: "Direct",
};

// Plan is fixed server-side; only the two metrics are offered.
const METRICS = ["walking", "direct"];
// Static label used for history entries (single fixed plan).
const PLAN_LABEL = "DC PET layout (LP1)";
const PLAN_ID = "6482-lp1";

export interface DistanceQueryPrefill {
  from?: string;
  to?: string;
  metric?: string;
  nonce?: number; // change to re-apply the same prefill
}

interface DistanceQueryFormProps {
  onResult?: (entry: DistanceHistoryEntry) => void;
  prefill?: DistanceQueryPrefill;
  /** unique id for the <datalist> so modal and full-page instances don't clash */
  datalistId?: string;
}

const DistanceQueryForm: React.FC<DistanceQueryFormProps> = ({
  onResult,
  prefill,
  datalistId = "distance-bays",
}) => {
  const { config } = useConfig();

  const apiBase = React.useMemo(
    () => resolveDistanceApiBase(config),
    [config]
  );

  const [metric, setMetric] = useState<string>("walking");
  const [bays, setBays] = useState<string[]>([]);

  const [fromBay, setFromBay] = useState("");
  const [toBay, setToBay] = useState("");

  const [result, setResult] = useState<DistanceResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [querying, setQuerying] = useState(false);

  // Load bay IDs (autocomplete) once. Plan is fixed server-side.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchDistanceBays(apiBase);
        if (!cancelled) setBays(list);
      } catch {
        if (!cancelled) setBays([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiBase]);

  // Apply prefill (e.g. re-running a history entry).
  useEffect(() => {
    if (!prefill) return;
    if (prefill.metric) setMetric(prefill.metric);
    if (prefill.from !== undefined) setFromBay(prefill.from);
    if (prefill.to !== undefined) setToBay(prefill.to);
    setResult(null);
    setErrorMessage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill?.nonce]);

  const handleCompute = useCallback(async () => {
    setResult(null);
    setErrorMessage(null);

    if (!fromBay.trim() || !toBay.trim()) {
      setErrorMessage("Please enter both bay IDs.");
      return;
    }

    setQuerying(true);
    try {
      const response = await queryDistance(apiBase, {
        from: fromBay.trim(),
        to: toBay.trim(),
        metric,
      });
      if (response.ok) {
        setResult(response.data);
        if (onResult) {
          onResult({
            id: `${Date.now()}-${response.data.from}-${response.data.to}-${response.data.metric}`,
            planId: PLAN_ID,
            planLabel: PLAN_LABEL,
            from: response.data.from,
            to: response.data.to,
            metric: response.data.metric,
            distance: response.data.distance,
            unit: response.data.unit,
            at: Date.now(),
          });
        }
      } else {
        const e = response.error;
        if (e.missing === "from") {
          setErrorMessage(`Bay ID "${e.value ?? fromBay}" does not exist on this floor plan.`);
        } else if (e.missing === "to") {
          setErrorMessage(`Bay ID "${e.value ?? toBay}" does not exist on this floor plan.`);
        } else {
          setErrorMessage(e.error || "Distance query failed.");
        }
      }
    } catch (err: any) {
      setErrorMessage(err?.message || "Unable to reach the distance service.");
    } finally {
      setQuerying(false);
    }
  }, [apiBase, fromBay, toBay, metric, onResult]);

  const handleSwap = () => {
    setFromBay(toBay);
    setToBay(fromBay);
    setResult(null);
    setErrorMessage(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCompute();
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Distance type toggle */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-slate-600">Distance type</label>
        <div className="inline-flex rounded-md border border-slate-300 overflow-hidden">
          {METRICS.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => {
                setMetric(m);
                setResult(null);
                setErrorMessage(null);
              }}
              className={`flex-1 px-3 py-1.5 text-sm transition-colors ${
                metric === m
                  ? "bg-primary-500 text-white"
                  : "bg-white text-slate-700 hover:bg-slate-100"
              }`}
            >
              {METRIC_LABELS[m] || m}
            </button>
          ))}
        </div>
        {metric === "walking" && (
          <span className="text-[11px] text-slate-400">
            Walking distance is directional (from → to).
          </span>
        )}
      </div>

      {/* Bay IDs */}
      <datalist id={datalistId}>
        {bays.map((b) => (
          <option key={b} value={b} />
        ))}
      </datalist>

      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-600">From bay</label>
          <input
            type="text"
            list={datalistId}
            value={fromBay}
            onChange={(e) => setFromBay(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g. 34M02"
            className="w-full border border-slate-300 rounded px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>
        <div className="flex justify-center -my-1">
          <button
            type="button"
            onClick={handleSwap}
            title="Swap from / to"
            className="p-1.5 rounded-full text-slate-500 hover:text-primary-600 hover:bg-slate-100 rotate-90"
          >
            <SwapIcon />
          </button>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-600">To bay</label>
          <input
            type="text"
            list={datalistId}
            value={toBay}
            onChange={(e) => setToBay(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g. 34L04"
            className="w-full border border-slate-300 rounded px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>
      </div>

      <button
        type="button"
        onClick={handleCompute}
        disabled={querying}
        className="w-full bg-primary-500 text-white rounded px-3 py-2 text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {querying ? "Computing…" : "Get distance"}
      </button>

      {/* Result field */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-slate-600">Result</label>
        {errorMessage ? (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
            {errorMessage}
          </div>
        ) : result ? (
          <div className="bg-slate-50 border border-slate-200 rounded px-3 py-3">
            <div className="text-2xl font-semibold text-slate-800">
              {result.distance}
              <span className="text-base font-normal text-slate-500 ml-1">{result.unit}</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {result.from} → {result.to} · {METRIC_LABELS[result.metric] || result.metric}
            </div>
          </div>
        ) : (
          <div className="text-sm text-slate-400 bg-slate-50 border border-dashed border-slate-200 rounded px-3 py-3">
            Enter two bay IDs and press “Get distance”.
          </div>
        )}
      </div>
    </div>
  );
};

export default DistanceQueryForm;
